import * as THREE from 'three'
import type { MeshResourceOwnership } from '@/plugins/mesh/types'
import type { Disposable } from '@/shared/types/core'

const MAX_TEXTURE_SCAN_DEPTH = 6

function isRecord(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

function collectTextures(
  value: unknown,
  textures: Set<THREE.Texture>,
  visited: Set<object>,
  depth: number
): void {
  if (value instanceof THREE.Texture) {
    textures.add(value)
    return
  }
  if (value === null || typeof value !== 'object' || depth > MAX_TEXTURE_SCAN_DEPTH) {
    return
  }
  if (visited.has(value)) return
  visited.add(value)

  if (Array.isArray(value)) {
    for (const item of value) {
      collectTextures(item, textures, visited, depth + 1)
    }
    return
  }

  if (isRecord(value)) {
    for (const item of Object.values(value)) {
      collectTextures(item, textures, visited, depth + 1)
    }
  }
}

/** 收集材质直接属性和 uniforms 中引用的纹理。 */
export function enumerateMaterialTextures(material: THREE.Material): Set<THREE.Texture> {
  const textures = new Set<THREE.Texture>()
  const visited = new Set<object>()

  for (const value of Object.values(material)) {
    collectTextures(value, textures, visited, 0)
  }

  return textures
}

/**
 * 静态 Mesh 资源所有权跟踪器。
 *
 * 所有资源按对象身份去重；释放时即使个别资源抛错，也会继续清理其余资源。
 */
export class MeshResourceTracker implements MeshResourceOwnership, Disposable {
  readonly geometries = new Set<THREE.BufferGeometry>()
  readonly materials = new Set<THREE.Material>()
  readonly textures = new Set<THREE.Texture>()
  readonly others = new Set<Disposable>()

  private _disposed = false

  /** 跟踪器是否已经执行过释放。 */
  get disposed(): boolean {
    return this._disposed
  }

  /** 登记 Geometry 所有权。 */
  trackGeometry<T extends THREE.BufferGeometry>(geometry: T): T {
    this.ensureActive()
    this.geometries.add(geometry)
    return geometry
  }

  /**
   * 登记 Material 的独占所有权，并接管其可达纹理。
   * 外部共享材质或纹理必须由调用方先克隆，不能同时交给多个 tracker。
   */
  trackMaterial<T extends THREE.Material>(material: T): T
  trackMaterial<T extends THREE.Material>(material: readonly T[]): readonly T[]
  trackMaterial<T extends THREE.Material>(material: T | readonly T[]): T | readonly T[] {
    this.ensureActive()
    const materials = Array.isArray(material) ? material : [material]
    for (const item of materials) {
      this.materials.add(item)
      for (const texture of enumerateMaterialTextures(item)) {
        this.textures.add(texture)
      }
    }
    return material
  }

  /** 登记 Texture 所有权。 */
  trackTexture<T extends THREE.Texture>(texture: T): T {
    this.ensureActive()
    this.textures.add(texture)
    return texture
  }

  /** 登记其他实现 dispose() 的资源。 */
  trackOther<T extends Disposable>(resource: T): T {
    this.ensureActive()
    if (resource instanceof THREE.BufferGeometry) {
      this.geometries.add(resource)
    } else if (resource instanceof THREE.Material) {
      this.trackMaterial(resource)
    } else if (resource instanceof THREE.Texture) {
      this.textures.add(resource)
    } else {
      this.others.add(resource)
    }
    return resource
  }

  /** 遍历 Object3D，登记 Mesh 的 Geometry、Material 和纹理。 */
  trackObject3D<T extends THREE.Object3D>(root: T): T {
    this.ensureActive()
    root.traverse((object) => {
      const renderable = object as THREE.Object3D & {
        geometry?: THREE.BufferGeometry
        material?: THREE.Material | THREE.Material[]
      }
      if (renderable.geometry instanceof THREE.BufferGeometry) {
        this.trackGeometry(renderable.geometry)
      }
      if (renderable.material instanceof THREE.Material) {
        this.trackMaterial(renderable.material)
      } else if (Array.isArray(renderable.material)) {
        this.trackMaterial(renderable.material)
      }
      if (object instanceof THREE.InstancedMesh && object.morphTexture) {
        this.trackTexture(object.morphTexture)
      }
    })
    return root
  }

  /** 合并另一份资源所有权，保持对象身份去重。 */
  adopt(ownership: MeshResourceOwnership): void {
    this.ensureActive()
    for (const geometry of ownership.geometries) this.geometries.add(geometry)
    for (const material of ownership.materials) this.trackMaterial(material)
    for (const texture of ownership.textures) this.textures.add(texture)
    for (const resource of ownership.others) this.trackOther(resource)
  }

  /**
   * 幂等释放全部拥有资源。
   *
   * @throws AggregateError 一个或多个资源释放失败时，在其余资源均已清理后抛出
   */
  dispose(): void {
    if (this._disposed) return
    this._disposed = true
    const errors: unknown[] = []

    this.disposeSet(this.geometries, errors)
    this.disposeSet(this.materials, errors)
    this.disposeSet(this.textures, errors)
    this.disposeSet(this.others, errors)

    if (errors.length > 0) {
      throw new AggregateError(errors, `释放 Mesh 资源失败，失败数量: ${errors.length}`)
    }
  }

  private disposeSet<T extends Disposable>(resources: Set<T>, errors: unknown[]): void {
    for (const resource of resources) {
      try {
        resource.dispose()
      } catch (error) {
        errors.push(error)
      }
    }
    resources.clear()
  }

  private ensureActive(): void {
    if (this._disposed) {
      throw new Error('MeshResourceTracker 已释放，不能继续登记资源')
    }
  }
}
